// src/pages/Dashboard.jsx
// الصفحة الرئيسية — نفس ترويسة القوائم (MenuHeader) ومربعات الأقسام بحجم أكبر، مع دعم الترجمة

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Paper, Typography } from "@mui/material";

import CategoryIcon from "@mui/icons-material/Category";
import TuneIcon from "@mui/icons-material/Tune";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import WbSunnyOutlinedIcon from "@mui/icons-material/WbSunnyOutlined";
import NightsStayOutlinedIcon from "@mui/icons-material/NightsStayOutlined";
import EventIcon from "@mui/icons-material/Event";
import StoreOutlinedIcon from "@mui/icons-material/StoreOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

import axios from "../../axios";
import { MenuHeader, MenuCard, useMenuT } from "../../components/Menu/MenuKit";
import { useAuth } from "../../context/AuthContext";

// كل قسم يظهر لمن يملك أي صلاحية تبدأ بأحد هذه المقاطع (المالك يرى الكل)
// ⚠️ الإخفاء هنا للترتيب فقط — الحماية الفعلية في السيرفر
const MODULES = [
  {
    key: "sales", nameKey: "dashboard.sales", nameFallback: "المبيعات", icon: <StorefrontOutlinedIcon />, color: "#4F46E5", link: "/sales-menu",
    perms: ["Invoice.Sales.", "Invoice.SalesReturn.", "Invoice.Quotation.", "Customer.", "Bond.Receipt."],
  },
  {
    key: "pos", nameKey: "dashboard.pos", nameFallback: "نقطة البيع", icon: <PointOfSaleIcon />, color: "#7C3AED", link: "/pos",
    perms: ["Invoice.Sales.Create", "Invoice.SalesReturn.Create"],
  },
  {
    key: "purchases", nameKey: "dashboard.purchases", nameFallback: "المشتريات", icon: <ShoppingCartOutlinedIcon />, color: "#E11D48", link: "/purchases-menu",
    perms: ["Invoice.Purchase.", "Invoice.PurchaseReturn.", "Supplier.", "Bond.Payment."],
  },
  {
    key: "warehouses", nameKey: "dashboard.warehouses", nameFallback: "المستودعات", icon: <WarehouseIcon />, color: "#059669", link: "/warehouses-menu",
    perms: ["Product.", "Category.", "Unit.", "Stock.", "Invoice.OpeningStock.", "Invoice.StockIn.", "Invoice.StockOut.", "Report.Inventory."],
  },
  {
    key: "accounting", nameKey: "dashboard.accounting", nameFallback: "المحاسبة", icon: <AccountBalanceIcon />, color: "#2563EB", link: "/accounts-menu",
    perms: ["ChartOfAccounts.", "JournalEntry.", "GlSettings.", "Report.", "Bond.", "VOUCHER_VIEW", "Safe.", "Bank."],
  },
  {
    key: "definitions", nameKey: "dashboard.definitions", nameFallback: "التعاريف", icon: <CategoryIcon />, color: "#0891B2", link: "/company-definitions",
    perms: ["Customer.", "Supplier.", "Product.", "Branch.", "Safe.", "Bank.", "PaymentMethod.", "User.", "Company."],
  },
  {
    key: "settings", nameKey: "dashboard.generalSettings", nameFallback: "الإعدادات", icon: <TuneIcon />, color: "#DB2777", link: "/settings-menu",
    perms: ["Settings.", "Zatca.", "InvoiceType.", "PrintLayout.", "Promotion.", "Pos.Devices.", "Role.", "Security.", "GlSettings."],
  },
];

// مفتاح ينتهي بنقطة = مقطع (أي صلاحية تبدأ به)، وإلا مطابقة تامة
const allows = (permissions, patterns) =>
  patterns.some(p => (p.endsWith(".") ? permissions.some(x => x.startsWith(p)) : permissions.includes(p)));

const chipSx = {
  display: "inline-flex", alignItems: "center", gap: .75, px: 1.5, py: .75, borderRadius: "11px",
  bgcolor: "rgba(255,255,255,.14)", border: "1px solid rgba(255,255,255,.28)", fontSize: 14, fontWeight: 700,
  "& svg": { fontSize: 18 },
};

// اسم الفرع المسجّل به الدخول (يُحفظ في المتصفح حتى لا يُطلب مع كل فتح)
function useCurrentBranchName() {
  const companyId = localStorage.getItem("companyId");
  const branchId = localStorage.getItem("branchId");
  const cacheKey = `branchName:${companyId}:${branchId}`;
  const [name, setName] = useState(() => {
    try { return localStorage.getItem(cacheKey) || ""; } catch { return ""; }
  });

  useEffect(() => {
    if (!companyId || !branchId) return undefined;
    let alive = true;
    axios.get(`/Branch/by-company/${companyId}`)
      .then(r => {
        const b = (Array.isArray(r.data) ? r.data : []).find(x => String(x.id) === String(branchId));
        const n = b ? (b.nameAr || b.name || "") : "";
        if (!alive || !n) return;
        setName(n);
        try { localStorage.setItem(cacheKey, n); } catch { /* تجاهل */ }
      })
      .catch(() => { /* يبقى الاسم المحفوظ */ });
    return () => { alive = false; };
  }, [companyId, branchId, cacheKey]);

  return name;
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { tr, dir, lang } = useMenuT();
  const branchName = useCurrentBranchName();
  const { permissions = [], isOwner } = useAuth() || {};

  const modules = useMemo(
    () => (isOwner ? MODULES : MODULES.filter(m => allows(permissions, m.perms))),
    [isOwner, permissions],
  );

  const userName = localStorage.getItem("userName") || "";
  const companyName = localStorage.getItem("companyName") || tr("dashboard.title", "لوحة التحكم الرئيسية");

  const hour = new Date().getHours();
  const morning = hour >= 4 && hour < 12;
  const greeting = morning ? tr("dashboard.goodMorning", "صباح الخير") : tr("dashboard.goodEvening", "مساء الخير");
  const eyebrow = userName ? `${greeting}${lang === "en" ? ", " : "، "}${userName}` : greeting;

  const today = new Date().toLocaleDateString(lang === "en" ? "en-GB" : "ar-SA-u-ca-gregory-nu-latn", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  });

  return (
    <Box dir={dir} sx={{ px: { xs: 1, sm: 2, md: 3 }, py: { xs: 1, md: 2 } }}>
      <MenuHeader
        icon={morning ? <WbSunnyOutlinedIcon /> : <NightsStayOutlinedIcon />}
        eyebrow={eyebrow}
        title={companyName}
      >
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {branchName && (
            <Box sx={chipSx} title={tr("dashboard.currentBranch", "الفرع الحالي")}>
              <StoreOutlinedIcon />{branchName}
            </Box>
          )}
          <Box sx={chipSx}>
            <EventIcon />{today}
          </Box>
        </Box>
      </MenuHeader>

      <Box sx={{
        display: "grid", gap: { xs: 1, sm: 1.5 },
        gridTemplateColumns: { xs: "repeat(2, minmax(0,1fr))", sm: "repeat(3, minmax(0,1fr))", md: "repeat(4, minmax(0,1fr))" },
      }}>
        {modules.map(m => <MenuCard key={m.key} item={m} size="lg" onClick={() => navigate(m.link)} />)}
      </Box>

      {modules.length === 0 && (
        <Paper elevation={0} sx={{ mt: 1, py: 6, px: 2, textAlign: "center", border: "1px dashed #cbd5e1", borderRadius: "14px" }}>
          <LockOutlinedIcon sx={{ fontSize: 40, color: "#cbd5e1" }} />
          <Typography sx={{ fontWeight: 800, mt: 1, color: "#334155" }}>
            {tr("dashboard.noModules", "لا توجد أقسام متاحة لحسابك")}
          </Typography>
          <Typography sx={{ fontSize: 13.5, color: "#64748b", mt: .5 }}>
            {tr("dashboard.noModulesHint", "تواصل مع مدير المنشأة لمنحك الصلاحيات المناسبة.")}
          </Typography>
        </Paper>
      )}
    </Box>
  );
}